<template>
  <div>
    <div class="ts-space-y-2">
      <div>
        <label class="ts-mb-0 required ts-font-robot">{{
          $t('task.title')
        }}</label>
        <div class="ts-space-y-2">
          <input v-model="model.title" type="text" class="form-control" />
          <div class="validate" v-if="errors.has('title')">
            {{ errors.first('title') }}
          </div>
        </div>
      </div>
      <div>
        <label class="ts-mb-0 ts-font-robot required">{{
          $t('task.status')
        }}</label>
        <div class="ts-space-y-2">
          <Select v-model="model.status_id" style="width:100%">
            <Option
              v-for="(item, index) in status"
              :value="item.status_id"
              :key="index"
              >{{ item.project_status }}</Option
            >
          </Select>
          <div class="validate" v-if="errors.has('status_id')">
            {{ errors.first('status_id') }}
          </div>
        </div>
      </div>
      <div>
        <label class="ts-mb-0 ts-font-robot">{{ $t('task.owner') }}</label>
        <div class="ts-space-y-2">
          <Select
            :filterable="true"
            v-model="model.response_by_id"
            style="width:100%"
          >
            <Option
              v-for="(item, index) in owners"
              :value="item.user_id"
              :key="index"
              >{{ item.employee_name_kh + ' ' + item.employee_name_en }}</Option
            >
          </Select>
          <div class="validate" v-if="errors.has('response_by_id')">
            {{ errors.first('response_by_id') }}
          </div>
        </div>
      </div>
      <div>
        <label class="ts-mb-0 ts-font-robot">{{ $t('task.estimated') }}</label>
        <div class="ts-space-y-2">
          <DatePicker
            style="width: 100%"
            :value="model.estimate_date"
            type="daterange"
            format="dd-MM-yyyy"
            @on-change="value => (model.estimate_date = value)"
          />
          <div class="validate" v-if="errors.has('estimate_date')">
            {{ errors.first('estimate_date') }}
          </div>
        </div>
      </div>
      <div>
        <label class="ts-mb-0 ts-font-robot">{{ $t('task.actual') }}</label>
        <div class="ts-space-y-2">
          <DatePicker
            style="width: 100%"
            :value="model.actual_date"
            type="daterange"
            format="dd-MM-yyyy"
            @on-change="value => (model.actual_date = value)"
          />
          <div class="validate" v-if="errors.has('actual_date')">
            {{ errors.first('actual_date') }}
          </div>
        </div>
      </div>
      <div>
        <label class="ts-mb-0 ts-font-robot">{{ $t('task.remark') }}</label>
        <div class="ts-space-y-2">
          <textarea
            v-model="model.remark"
            class="form-control"
            rows="3"
          ></textarea>
        </div>
      </div>
      <div
        class="ts-mt-4 ts-border-2 ts-border-dashed ts ts-py-3 ts-px-5 ts-h-24 ts-rounded-md ts-flex ts-items-center ts-space-x-1 ts-justify-center ts-overflow-y-scroll"
        :class="{ 'ts-border-blue-500': isDragOver }"
        @drop="handleOnDrop"
        @dragover.prevent="dragover"
        @dragleave.prevent="dragleave"
        @click.prevent="openFileBrowser"
      >
        <h2
          class="ts-text-gray-300 ts-font-robot ts-text-lg"
          v-if="!model.file"
        >
          <Icon type="md-folder" size="20" /> Drop file here ...
        </h2>
        <div v-else>
          <div v-if="isImage()">
            <img
              :src="readImagePreview()"
              alt="Preview"
              class="ts-w-16 ts-h-16 ts-object-cover ts-object-center"
            />
          </div>
          <div
            v-else-if="isPdf()"
            class="ts-flex ts-items-center ts-justify-center ts-w-full ts-h-full"
          >
            <i class="fas fa-file-pdf fa-3x"></i>
          </div>
          <div
            v-else-if="isExcel()"
            class="ts-flex ts-items-center ts-justify-center ts-w-full ts-h-full"
          >
            <i class="fas fa-file-excel fa-3x"></i>
          </div>
          <div
            v-else
            class="ts-flex ts-items-center ts-justify-center ts-w-full ts-h-full"
          >
            <i class="fas fa-file fa-3x"></i>
          </div>
        </div>
      </div>
      <div class="ts-flex ts-justify-end ts-space-x-3 ts-mt-3">
        <ts-button @click.prevent="$emit('close', false)">
          {{ $t('cancel') }}</ts-button
        >
        <ts-button color="primary" @click.prevent="onUpdate">
          {{ $t('update') }}</ts-button
        >
      </div>
    </div>
    <Spin size="large" fix v-if="loading"></Spin>
  </div>
</template>

<script>
import { Errors } from 'form-backend-validation'
import { mapActions } from 'vuex'
export default {
  props: ['edit_data'],
  data() {
    return {
      status: [],
      owners: [],
      show_modal: false,
      loading: false,
      isDragOver: false,
      errors: new Errors(),
      model: {
        task_profile_id: undefined,
        title: '',
        status_id: 1,
        response_by_id: '',
        estimate_date: [],
        actual_date: [],
        remark: '',
        file: undefined
      }
    }
  },
  created() {
    this.fetchModel()
    this.model.task_profile_id = this.edit_data.task_profile_id
  },
  methods: {
    ...mapActions('projectMgt/taskProfile', ['formViewModel']),
    ...mapActions('projectMgt/subTaskProfile', ['update']),
    fetchModel() {
      this.loading = true
      this.formViewModel({
        fnName: 'owner,status'
      })
        .then(response => {
          this.status = response.data.status
          this.owners = response.data.owner
        })
        .then(() => {
          this.setEditData()
        })
        .finally(() => (this.loading = false))
    },
    handleOnDrop(event) {
      event.preventDefault()
      const selectedFiles = event.dataTransfer.files[0]
      this.processFiles(selectedFiles)
      this.isDragOver = false
    },
    dragover() {
      this.isDragOver = true
    },
    dragleave() {
      this.isDragOver = false
    },
    handleFileSelection(event) {
      const selectedFile = event.target.files[0]
      this.processFiles(selectedFile)
    },
    processFiles(file) {
      this.model.file = file
    },
    isImage() {
      return this.model.file.type.includes('image')
    },
    isPdf() {
      return this.model.file.type === 'application/pdf'
    },
    isExcel() {
      return (
        this.model.file.type ===
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      )
    },
    readImagePreview() {
      return URL.createObjectURL(this.model.file)
    },
    onUpdate() {
      const data = this.serializeData()
      this.loading = true
      this.errors.clear()
      this.update({
        id: this.model.id,
        data: data
      })
        .then(response => {
          this.$toasted.success(response.message)
          this.$emit('close', false)
        })
        .catch(error => {
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => (this.loading = false))
    },
    serializeData() {
      const formData = new FormData()
      const keys = Object.keys(this.model)
      keys.forEach(key => {
        if (Array.isArray(this.model[key])) {
          for (let n = 0; n <= this.model[key].length - 1; n++) {
            formData.append(key + `[${n}]`, this.model[key][n])
          }
        } else {
          formData.append(key, this.model[key] || '')
        }
      })

      return formData
    },
    setEditData() {
      this.model.id = this.edit_data.id
      this.model.title = this.edit_data.title
      this.model.status_id = this.edit_data.status_id
      this.model.response_by_id = this.edit_data.response_by_id
      this.model.estimate_date = [
        this.edit_data.estimate_start_date,
        this.edit_data.estimate_end_date
      ]
      this.model.actual_date = [
        this.edit_data.actual_start_date,
        this.edit_data.actual_end_date
      ]
      this.model.remark = this.edit_data.remark
      this.model.file = undefined
    }
  }
}
</script>
